import { useEffect, useState, useRef } from "react"
import { ArrowPathIcon } from "@heroicons/react/24/solid"
import ProgressBar from "aldoo-ra/ProgressBar"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"

const LoadingOverlay = ({ title, loading, progress, message, log, activities, currentStatus, onComplete }) => {
  const [isOpen, setIsOpen] = useState(false)
  const isCompleted = currentStatus === "completed"
  const activitiesScrollRef = useRef(null)
  const logScrollRef = useRef(null)

  useEffect(() => {
    if (loading) {
      setIsOpen(true)
    } else if (isCompleted) {
      setIsOpen(true) // Keep open when completed
    } else {
      setIsOpen(false) // Close when not loading and not completed
    }
  }, [loading, isCompleted, currentStatus])

  // Auto-scroll activities to bottom when new activities are added
  useEffect(() => {
    if (activitiesScrollRef.current) {
      activitiesScrollRef.current.scrollTop = activitiesScrollRef.current.scrollHeight
    }
  }, [activities])

  // Auto-scroll log to bottom when new log entries are added
  useEffect(() => {
    if (logScrollRef.current) {
      logScrollRef.current.scrollTop = logScrollRef.current.scrollHeight
    }
  }, [log])

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={isCompleted ? () => {} : () => setIsOpen(false)}
      className="sm:w-[500px] h-full md:h-auto"
      zIndex={1000}
      title={isCompleted ? "Restore Complete" : (title || "Loading")}
    >
      {isCompleted ? (
        <div className="mb-8 mx-auto w-12 h-12 text-admin_success flex items-center justify-center">
          <span className="text-3xl">✓</span>
        </div>
      ) : (
        <ArrowPathIcon className="mb-8 mx-auto w-6 h-6 text-primary animate-spin" />
      )}
      <div className="text-center text-primary mb-10">
        {isCompleted ? (
          <div>
            <div className="text-admin_success font-semibold text-lg mb-2">
              Restore completed successfully!
            </div>
            <div className="text-admin_text_secondary text-sm">
              The backup has been restored successfully. Review the details below:
            </div>
          </div>
        ) : (
          typeof message === 'string' ? (
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
              {message || "Please wait while we load the data"}
            </pre>
          ) : (
            message || "Please wait while we load the data"
          )
        )}
      </div>

      {progress !== undefined && (
        <ProgressBar
          title={progress + "%"}
          variant="default"
          progress={progress}
        />
      )}

      {/* Current Status Section */}
      {currentStatus && (
        <div className="mt-4">
          <div className="text-admin_text_secondary text-sm mb-2">Current Status:</div>
          <div className="bg-admin_canvas p-3 rounded border">
            <div className="flex items-center gap-2">
              {currentStatus === "completed" ? (
                <span className="text-admin_success">✓</span>
              ) : currentStatus === "running" ? (
                <ArrowPathIcon className="w-4 h-4 text-admin_primary animate-spin" />
              ) : (
                <span className="text-admin_warning">⚠</span>
              )}
              <span className="text-sm text-admin_text capitalize">{currentStatus}</span>
            </div>
          </div>
        </div>
      )}

      {/* Activities Timeline */}
      {activities && activities.length > 0 && (
        <div className="mt-4">
          <div className="text-admin_text_secondary text-sm mb-2">Activity Timeline:</div>
          <div 
            ref={activitiesScrollRef}
            className="bg-admin_canvas p-3 rounded border max-h-40 overflow-y-auto"
            style={{ scrollBehavior: 'smooth' }}
          >
            {activities.map((activity, index) => (
              <div key={index} className="flex items-start gap-2 mb-2 last:mb-0">
                <div className="flex-shrink-0 mt-1">
                  {activity.completed ? (
                    <span className="text-admin_success text-xs">✓</span>
                  ) : (
                    <ArrowPathIcon className="w-3 h-3 text-admin_primary animate-spin" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-admin_text">
                    {activity.activity}
                  </div>
                  <div className="text-xs text-admin_text_secondary">
                    {activity.timestamp} • {activity.progress}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Operation Log */}
      {log && log.length > 0 && (
        <div className="mt-4">
          <div className="text-admin_text_secondary text-sm mb-2">Operation Log:</div>
          <div 
            ref={logScrollRef}
            className="bg-admin_canvas p-3 rounded border max-h-32 overflow-y-auto"
            style={{ scrollBehavior: 'smooth' }}
          >
            {log.map((logEntry, index) => (
              <div key={index} className="text-xs font-mono text-admin_text mb-1 last:mb-0">
                {logEntry}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add completion button when completed */}
      {isCompleted && (
        <div className="flex justify-center mt-6">
          <Button
            variant="admin_primary"
            onClick={() => {
              setIsOpen(false)
              onComplete && onComplete()
            }}
            className="px-8"
          >
            OK
          </Button>
        </div>
      )}
    </Modal>
  )
}

export default LoadingOverlay
